import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Calendar, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const blogPosts = [
  {
    id: 1,
    title: 'Les tendances web design 2024 à ne pas manquer',
    excerpt: 'Découvrez les dernières tendances en matière de design web qui domineront cette année.',
    category: 'Design',
    author: 'Marie Dupont',
    date: '15 Jan 2024',
    readTime: '5 min',
    image: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&q=80',
    slug: 'tendances-web-design-2024',
  },
  {
    id: 2,
    title: 'SEO : Guide complet pour débutants en 2024',
    excerpt: 'Tout ce que vous devez savoir pour optimiser votre référencement naturel.',
    category: 'SEO',
    author: 'Pierre Martin',
    date: '12 Jan 2024',
    readTime: '8 min',
    image: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=800&q=80',
    slug: 'guide-seo-debutants-2024',
  },
  {
    id: 3,
    title: 'Comment augmenter les conversions de votre e-commerce',
    excerpt: 'Stratégies éprouvées pour transformer vos visiteurs en clients.',
    category: 'E-commerce',
    author: 'Sophie Laurent',
    date: '10 Jan 2024',
    readTime: '6 min',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    slug: 'augmenter-conversions-ecommerce',
  },
  {
    id: 4,
    title: "L'importance de la vitesse de chargement pour votre site",
    excerpt: "Pourquoi la performance web est cruciale et comment l'optimiser.",
    category: 'Performance',
    author: 'Marie Dupont',
    date: '8 Jan 2024',
    readTime: '4 min',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    slug: 'importance-vitesse-chargement-site',
  },
  {
    id: 5,
    title: 'Intelligence artificielle et marketing digital',
    excerpt: "Comment l'IA transforme les stratégies marketing des entreprises.",
    category: 'Marketing',
    author: 'Pierre Martin',
    date: '5 Jan 2024',
    readTime: '7 min',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
    slug: 'intelligence-artificielle-marketing-digital',
  },
  {
    id: 6,
    title: 'Créer une identité de marque forte en 2024',
    excerpt: 'Les étapes clés pour développer une identité visuelle mémorable.',
    category: 'Branding',
    author: 'Sophie Laurent',
    date: '2 Jan 2024',
    readTime: '5 min',
    image: 'https://images.unsplash.com/photo-1634942537034-2531766767d1?w=800&q=80',
    slug: 'creer-identite-marque-forte-2024',
  },
];

const categories = ['Tous', 'Design', 'SEO', 'E-commerce', 'Marketing', 'Performance', 'Branding'];

export const Blog = () => {
  const pageTitle =
    'Blog Weblax | Conseils SEO, web design, marketing digital et création de site internet';
  const pageDescription =
    'Découvrez le blog de Weblax : articles, conseils et guides sur le SEO, le web design, la création de site internet, la performance web, le branding et le marketing digital.';
  const pageUrl = 'https://weblax.fr/blog';
  const pageImage = 'https://weblax.fr/og-image.jpg';

  const blogStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog Weblax',
    url: pageUrl,
    description: pageDescription,
    publisher: {
      '@type': 'Organization',
      name: 'Weblax',
      url: 'https://weblax.fr',
      logo: {
        '@type': 'ImageObject',
        url: 'https://weblax.fr/logo.png',
      },
    },
  };

  const itemListStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: blogPosts.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://weblax.fr/blog/${post.slug}`,
      name: post.title,
    })),
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="blog SEO, blog web design, blog marketing digital, création site internet, référencement naturel, performance web, branding, e-commerce, agence web, Weblax"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(blogStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(itemListStructuredData)}
        </script>
      </Helmet>

      <div data-testid="blog-page">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28 text-center">
              <p className="weblax-caption text-primary mb-4">Blog</p>
              <h1 className="weblax-heading-display mb-6 max-w-3xl mx-auto">
                Actualités & <span className="weblax-gradient-text">conseils</span>
              </h1>
              <p className="weblax-body-lg max-w-2xl mx-auto">
                Retrouvez nos articles, guides et conseils pour développer votre présence digitale.
              </p>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 border-b border-border">
          <div className="weblax-container">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category, index) => (
                <Button
                  key={index}
                  variant={index === 0 ? 'default' : 'outline'}
                  className={index === 0 ? 'weblax-btn-primary' : 'weblax-btn-secondary'}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="weblax-section">
          <div className="weblax-container">
            <Card className="weblax-card overflow-hidden">
              <div className="grid lg:grid-cols-2">
                <div className="aspect-video lg:aspect-auto relative overflow-hidden">
                  <img
                    src={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6 md:p-8 lg:p-12 flex flex-col justify-center">
                  <Badge className="w-fit mb-4">{blogPosts[0].category}</Badge>
                  <h2 className="weblax-heading-2 mb-4 hover:text-primary transition-colors cursor-pointer">
                    {blogPosts[0].title}
                  </h2>
                  <p className="weblax-body-lg mb-6">{blogPosts[0].excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {blogPosts[0].author}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {blogPosts[0].date}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {blogPosts[0].readTime}
                    </div>
                  </div>
                  <Button className="weblax-btn-primary w-fit">
                    Lire l'article
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="weblax-section pt-0">
          <div className="weblax-container">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.slice(1).map((post) => (
                <Card key={post.id} className="weblax-card-interactive overflow-hidden group">
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <Badge className="absolute top-4 left-4">{post.category}</Badge>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {post.author}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button variant="outline" className="weblax-btn-secondary">
                Charger plus d'articles
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="weblax-heading-1 mb-4">
                Restez informé
              </h2>
              <p className="weblax-body-lg mb-8">
                Recevez nos derniers articles et conseils directement dans votre boîte mail.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Votre email"
                  className="weblax-input flex-1"
                />
                <Button className="weblax-btn-primary">
                  S'inscrire
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;